import { useState } from 'react';
import { useDroppable } from '@dnd-kit/core';
import { ChevronsLeftRight } from 'lucide-react';
import type { LeadWithRelations } from '@/types';
import { Button } from '@/components/ui/button';
import { LeadsBoardCard } from './LeadsBoardCard';
import type { LeadStage, LeadStageConfig } from '@/components/leads/leadStages';
import { cn } from '@/lib/utils';

const CARDS_PER_PAGE = 50;

interface LeadsBoardColumnProps {
  stage: LeadStageConfig;
  leads: LeadWithRelations[];
  /** A drag is in progress and this column will take the dragged lead */
  accepts: boolean;
  dragging: boolean;
  collapsed?: boolean;
  onToggleCollapsed?: () => void;
  onOpenLead: (id: string) => void;
  onMove: (lead: LeadWithRelations, to: LeadStage) => void;
  selectedLeadId?: string | null;
}

export function LeadsBoardColumn({
  stage,
  leads,
  accepts,
  dragging,
  collapsed,
  onToggleCollapsed,
  onOpenLead,
  onMove,
  selectedLeadId,
}: LeadsBoardColumnProps) {
  const [limit, setLimit] = useState(CARDS_PER_PAGE);
  const { setNodeRef, isOver } = useDroppable({ id: stage.value, disabled: !accepts });
  const dimmed = dragging && !accepts;
  const highlighted = accepts && isOver;

  if (collapsed) {
    return (
      <button
        ref={setNodeRef}
        type="button"
        onClick={onToggleCollapsed}
        title={`Show ${stage.label}`}
        className={cn(
          'flex w-11 shrink-0 flex-col items-center gap-3 rounded-lg border border-dashed py-3 transition-colors',
          highlighted ? 'border-primary/60 bg-primary/5' : 'border-border bg-muted/20 hover:bg-muted/40',
          dimmed && 'opacity-40',
        )}
      >
        <span className={cn('h-2.5 w-2.5 rounded-full', stage.dotClass)} />
        <span className="text-[11px] font-semibold tabular-nums text-muted-foreground">{leads.length}</span>
        <span className="text-[13px] font-semibold [writing-mode:vertical-rl]">{stage.label}</span>
      </button>
    );
  }

  const shown = leads.slice(0, limit);
  const remaining = leads.length - shown.length;

  return (
    <div className={cn('flex w-72 shrink-0 flex-col transition-opacity', dimmed && 'opacity-40')}>
      <div className="mb-2 px-1">
        <div className="flex items-center gap-2">
          <span className={cn('h-2.5 w-2.5 shrink-0 rounded-full', stage.dotClass)} />
          <span className="flex-1 truncate text-[13px] font-semibold">{stage.label}</span>
          <span className="rounded-full bg-primary/10 px-1.5 py-0.5 text-[11px] font-semibold leading-none text-primary tabular-nums">
            {leads.length}
          </span>
          {onToggleCollapsed && (
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 text-muted-foreground"
              onClick={onToggleCollapsed}
              aria-label={`Collapse ${stage.label}`}
            >
              <ChevronsLeftRight className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
        {stage.hint && <p className="mt-1 text-[11px] text-muted-foreground">{stage.hint}</p>}
      </div>

      <div
        ref={setNodeRef}
        className={cn(
          'min-h-40 flex-1 space-y-2 rounded-lg border border-dashed p-2 transition-colors',
          highlighted ? 'border-primary/60 bg-primary/5' : 'border-border/60 bg-muted/20',
        )}
      >
        {shown.map((lead) => (
          <LeadsBoardCard
            key={lead.id}
            lead={lead}
            onOpen={onOpenLead}
            onMove={onMove}
            selected={selectedLeadId === lead.id}
          />
        ))}

        {leads.length === 0 && (
          <p className="py-8 text-center text-[12px] text-muted-foreground">
            {dragging && accepts ? 'Drop here' : 'No leads'}
          </p>
        )}

        {remaining > 0 && (
          <Button
            variant="link"
            className="h-auto w-full p-1 text-[12px] font-semibold tabular-nums"
            onClick={() => setLimit((prev) => prev + CARDS_PER_PAGE)}
          >
            Show {Math.min(remaining, CARDS_PER_PAGE)} more ({remaining} remaining)
          </Button>
        )}
      </div>
    </div>
  );
}
